import { ValueError } from "../error/ValueError.js";
import { Feedback } from "../feedback/Feedback.js";
import { Feedbacks } from "../feedback/Feedbacks.js";
import { getLastItem, isArray } from "./array.js";
import { getDataProps } from "./data.js";
import { getDictionaryItems } from "./dictionary.js";
import { isIterable } from "./iterate.js";
import { getUndefined } from "./undefined.js";
/** Get value that validates against a given `Validator`, or throw `ValidationError` */
export function getValid(unsafeValue, validator) {
    try {
        return validator.validate(unsafeValue);
    }
    catch (thrown) {
        if (thrown instanceof Feedback)
            throw new ValueError("Must validate", thrown);
        throw thrown;
    }
}
/** Assert that a value validates against a given `Validator`, or throw `ValidationError` */
export function assertValid(unsafeValue, validator) {
    getValid(unsafeValue, validator);
}
/**
 * Validate an iterable set of items with a validator.
 *
 * @yield Valid items.
 * @throw Feedback if one or more items did not validate.
 * - `feedback.details` will contain an entry for each invalid item (keyed by their count in the input iterable).
 */
export function* validateItems(unsafeItems, validator) {
    let index = 0;
    let valid = true;
    const feedbacks = {};
    for (const unsafeItem of unsafeItems) {
        try {
            yield validator.validate(unsafeItem);
        }
        catch (thrown) {
            if (!(thrown instanceof Feedback))
                throw thrown;
            feedbacks[index] = thrown;
            valid = false;
        }
        index++;
    }
    if (!valid)
        throw new Feedbacks(feedbacks, unsafeItems);
}
/**
 * Validate an array of items.
 *
 * @return Array with valid items.
 * @throw Feedback if one or more entry values did not validate.
 * - `feedback.details` will contain an entry for each invalid item (keyed by their count in the input iterable).
 */
export function validateArray(unsafeArray, validator) {
    let index = 0;
    let valid = true;
    let changed = true;
    const safeArray = [];
    const feedbacks = {};
    for (const unsafeItem of unsafeArray) {
        try {
            const safeItem = validator.validate(unsafeItem);
            safeArray.push(safeItem);
            if (!changed && safeItem !== unsafeItem)
                changed = true;
        }
        catch (thrown) {
            if (!(thrown instanceof Feedback))
                throw thrown;
            feedbacks[index] = thrown;
            valid = false;
        }
        index++;
    }
    if (!valid)
        throw new Feedbacks(feedbacks, unsafeArray);
    return changed || !isArray(unsafeArray) ? safeArray : unsafeArray;
}
/**
 * Validate the values of the entries in a dictionary object.
 *
 * @throw Feedback if one or more entry values did not validate.
 * - `feedback.details` will contain an entry for each invalid item (keyed by their count in the input iterable).
 */
export function validateDictionary(unsafeDictionary, validator) {
    let valid = true;
    let changed = false;
    const safeDictionary = {};
    const feedbacks = {};
    for (const [key, unsafeValue] of getDictionaryItems(unsafeDictionary)) {
        try {
            const safeValue = validator.validate(unsafeValue);
            safeDictionary[key] = safeValue;
            if (!changed && safeValue !== unsafeValue)
                changed = true;
        }
        catch (thrown) {
            if (!(thrown instanceof Feedback))
                throw thrown;
            feedbacks[key] = thrown;
            valid = false;
        }
    }
    if (!valid)
        throw new Feedbacks(feedbacks, unsafeDictionary);
    return changed || isIterable(unsafeDictionary) ? safeDictionary : unsafeDictionary;
}
/**
 * Validate a data object with a set of validators.
 * - Defined props in the object will be validated against the corresponding validator.
 * - `undefined` props in the object will be set to the default value of that prop.
 *
 * @return Valid object.
 * @throw Feedback if one or more props did not validate.
 * - `feedback.details` will contain an entry for each invalid item (keyed by their count in the input iterable).
 */
export function validateData(unsafeData, validators) {
    const { partial = false, id = false } = getValidationContext();
    let valid = true;
    let changed = true;
    const safeData = id && typeof unsafeData.id === "string" ? { id: unsafeData.id } : {};
    const feedbacks = {};
    for (const [key, validator] of getDataProps(validators)) {
        const unsafeValue = unsafeData[key];
        if (unsafeValue === undefined && partial)
            continue; // Silently skip `undefined` props if in partial mode.
        try {
            const safeValue = validator.validate(unsafeValue);
            safeData[key] = safeValue;
            if (!changed && safeValue !== unsafeValue)
                changed = true;
        }
        catch (thrown) {
            if (!(thrown instanceof Feedback))
                throw thrown;
            feedbacks[key] = thrown;
            valid = false;
        }
    }
    if (!valid)
        throw new Feedbacks(feedbacks, unsafeData);
    return changed ? safeData : unsafeData;
}
/** Store a list of current contexts. */
const CONTEXTS = [{}];
/** Get the current validation context. */
export function getValidationContext() {
    return getLastItem(CONTEXTS);
}
export function validateWithContext(unsafeValue, validator, context) {
    CONTEXTS.push(context);
    const validValue = validator.validate(unsafeValue);
    CONTEXTS.pop();
    return validValue;
}
// Undefined validator always returns undefined.
export const UNDEFINED_VALIDATOR = { validate: getUndefined };
